import React, { useState, useMemo, useContext, useCallback } from 'react'
import { groupBy, orderBy, flatten, keyBy } from 'lodash'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTeamAuditData } from 'app/frontend/base/config/queries'
import { LootContext } from '../context'
import {
  difficulties,
  difficultiesTrackLabels,
} from 'app/frontend/types/custom/Difficulty'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import Card from 'app/frontend/base/components/Card'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Dices } from 'lucide-react'
import TeamWish from './TeamWish'
import ItemEvaluation, { parseItemHash } from './ItemEvaluation'
import { useLocation } from 'react-router-dom'
import LinkButton from '@/components/ui/link-button'
import {
  seasonWishlistMetadata,
  VOIDFORGED_BONUS_ID,
  VOIDFORGED_ILVL,
  VOIDFORGED_SLOTS,
} from '../helpers'
import {
  TeamWishlistItem,
  TeamWishlistItemCharacterWishlist,
} from 'app/frontend/types/custom/TeamWishlistEncounter'
import UpgradeTrack, {
  colorByTrack,
} from 'app/frontend/types/custom/UpgradeTrack'
import {
  isChoiceObtained,
  TrinketRank,
  trinketRanks,
} from './trinketPriorityHelpers'

type Props = {
  teamWishlist: wowaudit.TeamWishlist
  selectedItemId?: number
  listHash: string | null
  onSelectItem: (itemId: number) => void
  onClearItem: (hash: string) => void
}

const TeamWishlistEncounters = ({
  teamWishlist,
  selectedItemId,
  listHash,
  onSelectItem,
  onClearItem,
}: Props) => {
  const { team } = useTeam()
  const { hash: initialHash } = useLocation()
  const { difficulty, valueDisplay, selectedSeason } = useContext(LootContext)
  const difficultyIndex = difficulties.indexOf(difficulty)
  const trinketPriorityEnabled = team.settings.trinketPriorityEnabled.value
  const { data: auditData } = useTeamAuditData(
    trinketPriorityEnabled ? team.id : undefined,
  )
  const minimumTrack = team.settings.tierPriorityMinimumTrack
    .value as UpgradeTrack

  const charactersById = useMemo(
    () =>
      keyBy(
        team.members?.map((member) => member.characterReference) || [],
        'id',
      ),
    [],
  )

  const visibleMembers = useMemo(
    () =>
      team.members?.filter((member) => member.teamRank?.wishlist_visibility) ??
      [],
    [team.members],
  )

  const priorityByCharacterForItems = useCallback(
    (itemIds: number[]): Record<number, TrinketRank> => {
      if (!trinketPriorityEnabled) return {}

      const itemIdSet = new Set(itemIds)
      const result: Record<number, TrinketRank> = {}

      visibleMembers.forEach((member) => {
        const characterId = member.characterReference.id
        const storedPriority = seasonWishlistMetadata(
          member.characterReference.wishlistMetadata,
          selectedSeason.id,
        ).trinket_priority
        const trinketCache =
          auditData?.[characterId]?.trinket_cache?.[String(selectedSeason.id)]

        for (const rank of trinketRanks) {
          const choice = storedPriority[rank]
          if (!choice?.item_id || !itemIdSet.has(choice.item_id)) continue
          if (isChoiceObtained(choice, trinketCache, minimumTrack)) continue
          result[characterId] = rank
          break
        }
      })

      return result
    },
    [
      trinketPriorityEnabled,
      visibleMembers,
      selectedSeason.id,
      auditData,
      minimumTrack,
    ],
  )

  const bonusRollCharactersByEncounter = useMemo(() => {
    const map: Record<number, wowaudit.CharacterReference[]> = {}
    if (!selectedSeason.bonus_rolls_available) return map

    visibleMembers.forEach((member) =>
      member.characterReference.bonusRollEncounterIds.forEach((encounterId) => {
        ;(map[encounterId] ??= []).push(member.characterReference)
      }),
    )
    Object.values(map).forEach((chars) =>
      chars.sort((a, b) => a.name.localeCompare(b.name)),
    )
    return map
  }, [visibleMembers, selectedSeason.bonus_rolls_available])

  const encounterFromHash = (hash: string | null) => {
    if (!hash || parseItemHash(hash)) return undefined
    if (hash === '#all') return 'all' as const
    return teamWishlist.encounters.find(
      (encounter) => `#${encounter.id}` === hash,
    )
  }

  const activeEncounter =
    encounterFromHash(listHash) ??
    encounterFromHash(initialHash) ??
    teamWishlist.encounters[0]

  const itemEncounter =
    selectedItemId == null
      ? undefined
      : teamWishlist.encounters.find((encounter) =>
          encounter.items.some((item) => item.id === selectedItemId),
        )

  const selectedDetailItem = itemEncounter?.items.find(
    (item) => item.id === selectedItemId,
  )

  const displayedEncounter = itemEncounter
    ? itemEncounter
    : activeEncounter === 'all'
    ? ('all' as const)
    : teamWishlist.encounters.find((encounter) =>
        activeEncounter.id != null
          ? encounter.id === activeEncounter.id
          : encounter.name === activeEncounter.name,
      ) ?? teamWishlist.encounters[0]

  const backToList = () => {
    onClearItem(itemEncounter ? `#${itemEncounter.id}` : '#all')
  }

  const [showTrackUpgrades, setShowTrackUpgrades] = useState(
    !localStorage.getItem('hidingTrackUpgrades'),
  )

  const toggleTrackUpgrades = () => {
    if (showTrackUpgrades) {
      localStorage.setItem('hidingTrackUpgrades', 'true')
      setShowTrackUpgrades(false)
    } else {
      localStorage.removeItem('hidingTrackUpgrades')
      setShowTrackUpgrades(true)
    }
  }

  const baseWowheadUrl = `https://wowhead.com/`

  // Build a variant-specific copy of a wishlist by filtering its wishes to the
  // requested variant and swapping in the matching score/alternative data.
  // Returns null when the character has no wishes for this variant.
  const buildVariantWishlist = (
    wishlist: TeamWishlistItemCharacterWishlist,
    isVoidforged: boolean,
  ): TeamWishlistItemCharacterWishlist | null => {
    const wishes = wishlist.wishes.filter(
      (wish) => !!wish.voidforged === isVoidforged,
    )
    if (wishes.length === 0) return null

    return {
      ...wishlist,
      wishes,
      score_by_spec:
        (isVoidforged
          ? wishlist.voidforged_score_by_spec
          : wishlist.score_by_spec) ?? wishlist.score_by_spec,
      alternative: isVoidforged
        ? wishlist.voidforged_alternative
        : wishlist.alternative,
      edited: !wishes.every((wish) =>
        ['raidbots', 'qe'].includes(wish.wish_type),
      ),
      outdated: wishes.map((wish) => wish.outdated).find(Boolean) || null,
    }
  }

  const renderItemBlock = (
    items: TeamWishlistItem[],
    isVoidforged: boolean,
  ) => {
    const rawWishlists = flatten(items.map((item) => item.wishlists))
    const wishlists = rawWishlists
      .map((wishlist) => buildVariantWishlist(wishlist, isVoidforged))
      .filter((wishlist): wishlist is TeamWishlistItemCharacterWishlist =>
        Boolean(wishlist),
      )
    const charactersWithWish = rawWishlists.map((w) => w.character_id)
    const trackUpgrades = isVoidforged
      ? []
      : teamWishlist.metadata.filter(
          (character) =>
            showTrackUpgrades &&
            !charactersWithWish.includes(character.id) &&
            items[0].slot &&
            character.current_tracks[items[0].slot] &&
            (character.current_tracks[items[0].slot] as number) >
              difficultyIndex &&
            items.filter((item) =>
              item.eligible_classes.includes(character.class_id),
            ).length &&
            items.filter((item) =>
              item.eligible_specs.includes(character.default_spec_id ?? 0),
            ).length &&
            (items[0].slot !== 'trinket' ||
              character.current_tracks[items[0].slot] === 6),
        )

    const priorityByCharacterId =
      !isVoidforged && items[0].slot === 'trinket'
        ? priorityByCharacterForItems(items.map((item) => item.id))
        : {}
    const renderedCharacterIds = new Set([
      ...wishlists.map((wishlist) => wishlist.character_id),
      ...trackUpgrades.map((character) => character.id),
    ])
    const priorityOnlyCharacterIds = Object.keys(priorityByCharacterId)
      .map(Number)
      .filter((characterId) => !renderedCharacterIds.has(characterId))
      .sort((a, b) =>
        (charactersById[a]?.name ?? '').localeCompare(
          charactersById[b]?.name ?? '',
        ),
      )

    return (
      <div
        key={`${items[0].id}${isVoidforged ? '-vf' : ''}`}
        className='mb-6 mt-2'
      >
        <div className='flex items-center gap-2 border-b pb-1.5'>
          {items.map((item, i) => {
            const bonusIds = isVoidforged
              ? [VOIDFORGED_BONUS_ID[difficultyIndex]]
              : item.bonus_ids

            return (
              <div className='flex items-center gap-1' key={item.id}>
                {i > 0 ? <span>/</span> : null}
                <a
                  target='_blank'
                  className={`inline-flex items-center gap-3 text-quality-${item.quality} text-[1rem]`}
                  rel='noreferrer'
                  href={`${baseWowheadUrl}item=${item.id}?ilvl=${
                    isVoidforged
                      ? VOIDFORGED_ILVL[difficultyIndex]
                      : item.item_level
                  }${bonusIds.length ? `&bonus=${bonusIds.join(':')}` : ''}`}
                >
                  {i === 0 ? (
                    <ItemIcon id={item.id} className='w-6 h-6' />
                  ) : null}

                  {item.name}
                </a>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  className='ml-3 h-6 px-2 py-0 text-xs'
                  onClick={() => onSelectItem(item.id)}
                >
                  Details
                </Button>
              </div>
            )
          })}
          {isVoidforged ? (
            <Card className='flex items-center ml-2 gap-2 py-0 pl-0 border-quality-epic/70'>
              <img
                src='https://data.wowaudit.com/img/voidforge.jpg'
                className='w-6 h-6 min-w-6 min-h-6 rounded-l-md'
              />
              <span>Voidforged</span>
            </Card>
          ) : null}
        </div>

        <div className='grid grid-cols-1 min-[750px]:grid-cols-2 min-[1000px]:grid-cols-3 min-[1300px]:grid-cols-4 gap-2 my-2'>
          {wishlists.length ||
          trackUpgrades.length ||
          priorityOnlyCharacterIds.length ? (
            <React.Fragment>
              {wishlists
                .sort(
                  (a, b) =>
                    (b.score_by_spec.total[
                      valueDisplay === 'absolute' ? 'score' : 'percentage'
                    ] as number) -
                    (a.score_by_spec.total[
                      valueDisplay === 'absolute' ? 'score' : 'percentage'
                    ] as number),
                )
                .map((wishlist) => (
                  <TeamWish
                    key={wishlist.character_id}
                    characterInfo={charactersById[wishlist.character_id]}
                    wishlist={wishlist}
                    currentTrack={
                      !isVoidforged && showTrackUpgrades
                        ? wishlist.equipped_track
                        : null
                    }
                    equippedItemLevels={
                      teamWishlist.metadata.find(
                        (m) => m.id === wishlist.character_id,
                      )?.equipped_item_levels
                    }
                    item={items[0]}
                    trinketPriorityRank={
                      priorityByCharacterId[wishlist.character_id]
                    }
                  />
                ))}
              {trackUpgrades.map((character) => (
                <TeamWish
                  key={character.id}
                  characterInfo={charactersById[character.id]}
                  currentTrack={
                    items[0].slot
                      ? character.current_tracks[items[0].slot]
                      : null
                  }
                  equippedItemLevels={character.equipped_item_levels}
                  item={items[0]}
                  trinketPriorityRank={priorityByCharacterId[character.id]}
                />
              ))}
              {priorityOnlyCharacterIds.map((characterId) => (
                <TeamWish
                  key={characterId}
                  characterInfo={charactersById[characterId]}
                  currentTrack={null}
                  item={items[0]}
                  trinketPriorityRank={priorityByCharacterId[characterId]}
                />
              ))}
            </React.Fragment>
          ) : (
            <div className='italic text-muted-foreground'>Not an upgrade</div>
          )}
        </div>
      </div>
    )
  }

  const activeItems =
    displayedEncounter === 'all'
      ? flatten(teamWishlist.encounters.map((e) => e.items))
      : displayedEncounter.items

  const groupedActiveItems = Object.values(
    groupBy(
      orderBy(activeItems, (i) => i.name),
      (i) => i.name,
    ),
  )

  return (
    <div className='flex gap-2'>
      <div className='min-[625px]:w-56 min-[625px]:min-w-56 min-[625px]:max-w-56 mt-2'>
        <ul className='border-r-4'>
          {teamWishlist.encounters.map((encounter) => {
            const totalValue =
              (valueDisplay === 'percentage'
                ? encounter.total_percentage_value
                : encounter.total_absolute_value) ?? 0

            return (
              <li
                key={encounter.name}
                className={`flex gap-2 py-2 cursor-pointer hover:bg-accent ml-[-1rem] pl-[1.2rem] mr-[-4px] pr-3 border-r-4 ${
                  displayedEncounter === encounter
                    ? 'bg-accent border-primary'
                    : 'border-transparent hover:border-border'
                }`}
                onClick={() => onClearItem(`#${encounter.id}`)}
              >
                <img
                  src={encounter.thumbnail_url}
                  className='w-10 h-10 min-w-10 min-h-10 rounded-full'
                />
                <div className='text-[15px] hidden min-[625px]:block'>
                  <div>{encounter.name}</div>
                  <div className='flex items-center gap-2'>
                    {totalValue > 0 ? (
                      <span className='text-success-foreground'>
                        {valueDisplay === 'percentage'
                          ? `${totalValue.toLocaleString('en-US', {
                              maximumFractionDigits: 2,
                              minimumFractionDigits: 2,
                            })}%`
                          : totalValue.toLocaleString('en-US', {
                              maximumFractionDigits: 0,
                            })}
                      </span>
                    ) : encounter.highest_manual_upgrade ? (
                      <span
                        className={`priority ${encounter.highest_manual_upgrade.toLowerCase()}`}
                      />
                    ) : (
                      <span className='text-muted-foreground'>nothing</span>
                    )}
                    {encounter.id &&
                    bonusRollCharactersByEncounter[encounter.id]?.length ? (
                      <span className='flex items-center gap-1 text-muted-foreground'>
                        <Dices size={14} className='text-info-foreground' />
                        {bonusRollCharactersByEncounter[encounter.id].length}
                      </span>
                    ) : null}
                  </div>
                </div>
              </li>
            )
          })}
          <li
            className={`flex gap-2 py-2 cursor-pointer hover:bg-accent ml-[-1rem] pl-[1.2rem] pr-3 mr-[-4px] border-r-4 ${
              displayedEncounter === 'all'
                ? 'bg-accent border-primary'
                : 'border-transparent hover:border-border'
            }`}
            onClick={() => onClearItem('#all')}
          >
            All items
          </li>
        </ul>
      </div>
      <section className='min-w-0 grow py-3 pl-3'>
        <div className='flex justify-between items-center pb-2 gap-2 flex-wrap'>
          {selectedDetailItem ? (
            <div className='flex flex-wrap items-center gap-2'>
              <a
                target='_blank'
                rel='noreferrer'
                href={`${baseWowheadUrl}item=${selectedDetailItem.id}?ilvl=${
                  selectedDetailItem.item_level
                }${
                  selectedDetailItem.bonus_ids.length
                    ? `&bonus=${selectedDetailItem.bonus_ids.join(':')}`
                    : ''
                }`}
                className={`inline-flex items-center gap-2 text-[1rem] font-medium text-quality-${selectedDetailItem.quality}`}
              >
                <ItemIcon id={selectedDetailItem.id} className='h-6 w-6' />
                {selectedDetailItem.name}
              </a>
              <Badge
                variant='outline'
                className={cn(
                  'px-2 py-0 font-normal',
                  colorByTrack[difficultiesTrackLabels[difficulty]],
                )}
              >
                {difficultiesTrackLabels[difficulty]}
              </Badge>
              <span className='font-medium'>
                {selectedDetailItem.item_level}
              </span>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='ml-3 h-6 px-2 py-0 text-xs'
                onClick={backToList}
              >
                Back to list
              </Button>
            </div>
          ) : (
            <h3 className='font-medium mb-0'>
              {displayedEncounter === 'all'
                ? 'All items'
                : displayedEncounter.name}
            </h3>
          )}
          {selectedDetailItem ? null : (
            <Button
              size='sm'
              variant='outline'
              className='w-40'
              onClick={toggleTrackUpgrades}
            >
              {showTrackUpgrades ? 'Hide' : 'Show'} track upgrades
            </Button>
          )}
        </div>

        {selectedDetailItem ? (
          <ItemEvaluation
            teamWishlist={teamWishlist}
            itemId={selectedDetailItem.id}
          />
        ) : displayedEncounter !== 'all' &&
          displayedEncounter.id &&
          bonusRollCharactersByEncounter[displayedEncounter.id]?.length ? (
          <Card className='mb-4 p-3'>
            <h5 className='flex items-center gap-2 mb-2'>
              <Dices size={18} className='text-info-foreground' />
              Bonus roll target
            </h5>
            <div className='flex flex-wrap gap-2'>
              {bonusRollCharactersByEncounter[displayedEncounter.id].map(
                (character) => (
                  <LinkButton
                    size='sm'
                    key={character.id}
                    to={`${team.path}/loot/characters/${character.id}`}
                    className='flex items-center gap-1.5'
                  >
                    <CharacterClassIcon
                      characterClass={character.characterClass}
                    />
                    {character.name}
                  </LinkButton>
                ),
              )}
            </div>
          </Card>
        ) : null}

        {selectedDetailItem ? null : activeItems.length === 0 ? (
          <p className='text-center text-muted-foreground py-12'>
            Wishlists will be available for this season soon
          </p>
        ) : (
          groupedActiveItems.map((items) => {
            const displaySlot = items[0].equipment_rule ?? items[0].slot
            const showVoidforged =
              teamWishlist.allowVoidforged &&
              !!displaySlot &&
              VOIDFORGED_SLOTS.includes(displaySlot) &&
              ['mythic', 'heroic'].includes(difficulty)

            return (
              <React.Fragment key={items[0].id}>
                {renderItemBlock(items, false)}
                {showVoidforged ? renderItemBlock(items, true) : null}
              </React.Fragment>
            )
          })
        )}
      </section>
    </div>
  )
}

export default TeamWishlistEncounters
